import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import {
  autocompleteSearchRequired,
  autocompletePlaceSelected,
  composeValidators,
} from '../../../../util/validators';
import { LISTING_PAGE_PARAM_TYPE_EDIT } from '../../../../util/urlHelpers';

// Import shared components
import {
  Form,
  FieldLocationAutocompleteInput,
} from '../../../../components';
import FormButtons from '../FormButtons/FormButtons';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import useInterfaceLang from '../../../../hooks/useInterfaceLang';

// Import modules from this directory
import css from './EditListingLocationForm.module.css';

const identity = v => v;

/**
 * The EditListingLocationForm component.
 *
 * @component
 * @param {Object} props
 * @param {string} props.formId - The form id
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} props.autoFocus - Whether the form is auto focused
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.ready - Whether the form is ready
 * @param {boolean} props.updated - Whether the form is updated
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {Object} props.fetchErrors - The fetch errors object
 * @param {string} props.saveActionMsg - The save action message
 * @param {Function} props.onSubmit - The submit function
 * @param {Object} props.errors - The errors object
 * @param {propTypes.error} props.errors.showListingsError - The show listings error
 * @param {propTypes.error} props.errors.updateListingError - The update listing error
 * @returns {JSX.Element}
 */
export const EditListingLocationForm = props => {
  const { language } = useInterfaceLang();
  return (
    <FinalForm
      {...props}
      render={formRenderProps => {
        const {
          formId = 'EditListingLocationForm',
          autoFocus,
          className,
          rootClassName,
          disabled,
          ready,
          handleSubmit,
          invalid,
          pristine,
          saveActionMsg,
          updated,
          updateInProgress = false,
          fetchErrors,
          values,
          onGoBackATab,
          form: formApi,
          draftingInProgress,
          disableSubmitOnly,
          listing,
        } = formRenderProps;

        const location = useLocation();
        const intl = useIntl();
        const addressRequiredMessage = intl.formatMessage({
          id: 'EditListingLocationForm.addressRequired',
        });
        const addressNotRecognizedMessage = intl.formatMessage({
          id: 'EditListingLocationForm.addressNotRecognized',
        });

        const optionalText = intl.formatMessage({
          id: 'EditListingLocationForm.optionalText',
        });

        const { updateListingError, showListingsError } = fetchErrors || {};

        const classes = classNames(rootClassName || css.root, className);
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled = invalid || disabled || submitInProgress;

        const canPublish = !listing?.attributes?.publicData?.bulkId;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {updateListingError ? (
              <p className={css.error}>
                <FormattedMessage id="EditListingLocationForm.updateFailed" />
              </p>
            ) : null}

            {showListingsError ? (
              <p className={css.error}>
                <FormattedMessage id="EditListingLocationForm.showListingFailed" />
              </p>
            ) : null}

            <FieldLocationAutocompleteInput
              rootClassName={css.locationAddress}
              inputClassName={css.locationAutocompleteInput}
              iconClassName={css.locationAutocompleteInputIcon}
              predictionsClassName={css.predictionsRoot}
              validClassName={css.validLocation}
              autoFocus={autoFocus}
              name="location"
              label={`${intl.formatMessage({ id: 'EditListingLocationForm.address' })}*`}
              placeholder={intl.formatMessage({
                id: 'EditListingLocationForm.addressPlaceholder',
              })}
              useDefaultPredictions={false}
              format={identity}
              valueFromForm={values.location}
              types={['place']}
              language={language}
              validate={composeValidators(
                autocompleteSearchRequired(addressRequiredMessage),
                autocompletePlaceSelected(addressNotRecognizedMessage)
              )}
            />

            <FormButtons
              draftingInProgress={draftingInProgress}
              saveActionMsg={saveActionMsg}
              submitDisabled={submitDisabled}
              submitInProgress={submitInProgress}
              submitReady={submitReady}
              formApi={formApi}
              onGoBackATab={onGoBackATab}
              disableSubmitButtonOnly={disableSubmitOnly || !!location?.state?.from}
              canPublish={canPublish}
            />
          </Form>
        );
      }}
    />
  );
};

export default EditListingLocationForm;
